<template>
  <modal
    ref="SpaCancelModal"
    :value="isOpen"
    variant="default"
    no-footer
    class="spa-reservation-modals__cancel-modal"
    :aria-labelled-by="modalTitleId"
    @input="handleOpenModal"
    @close="handleCloseModal"
  >
    <template #default="{ activate }">
      <slot v-bind="{ activate }" />
    </template>
    <template #header>
      <h6
        :id="modalTitleId"
        class="spa-reservation-modals__cancel-modal__title"
      >
        {{ title }}
      </h6>
    </template>

    <template #body="{ deactivate }">
      <div class="spa-reservation-modals__cancel-modal__body">
        <div class="spa-reservation-modals__cancel-modal__body-content">
          <p>{{ confirmationMessage }}</p>
        </div>
      </div>
      <div class="spa-reservation-modals__cancel-modal__footer">
        <button
          class="button--primary black size-md primary"
          :class="[{ loading: isSpaLoading }]"
          @click="handleConfirm(deactivate)"
          @keydown.space.prevent="handleConfirm(deactivate)"
          @keydown.enter.prevent="handleConfirm(deactivate)"
        >
          <span>{{ buttonText }}</span>
          <div
            class="sr-only"
            role="status"
          >
            {{ isSpaLoading ? tc('accessibility.loadingButtonStatus') : '' }}
          </div>
        </button>
      </div>
    </template>
  </modal>
</template>

<script>
  import { mapState, mapGetters, mapActions } from 'vuex'

  export default {
    name: 'SpaCancelModal',
    props: {
      /**
       * Modal type: To cancel only one treatment use 'treatment' or the whole booking use 'booking'
       */
      type: {
        type: String,
        required: true,
        validator: (value) => ['treatment', 'booking'].includes(value)
      },
      /**
       * Treatment to be canceled
       */
      treatment: {
        type: Object,
        default: () => ({})
      },
      /**
       * Guest number that the treatment belongs to (only used if type is 'treatment')
       */
      guestNumber: {
        type: Number,
        default: null
      }
    },
    data: () => ({
      isOpen: false
    }),
    computed: {
      ...mapGetters('i18n', ['tc']),
      ...mapState('spaSoft', ['isSpaLoading', 'reservationDetailsData']),

      modalTitleId() {
        return this.type === 'treatment'
          ? 'spa-cancel-treatment-title'
          : 'spa-cancel-reservation-title'
      },
      title() {
        return this.type === 'treatment' ? this.tc('spaSoft.reservationdetails.cancelPopup.treatmentTitle') : this.tc('spaSoft.reservationdetails.cancelPopup.bookingTitle')
      },
      confirmationMessage() {
        return this.type === 'treatment'
          ? this.tc('spaSoft.reservationdetails.cancelPopup.cancelTreatmentDescription', { treatmentName: this.treatment.sitecoreTreatmentDetails.title })
          : this.tc('spaSoft.reservationdetails.cancelPopup.cancelBookingDescription')
      },
      buttonText() {
        return this.type === 'treatment' ? this.tc('spaSoft.reservationdetails.cancelPopup.cancelTreatmentCta') : this.tc('spaSoft.reservationdetails.cancelPopup.cancelBookingCta')
      }
    },
    methods: {
      ...mapActions('spaSoft', ['cancelTreatments']),

      handleCloseModal() {
        document.body.classList.remove('no-scroll')
        this.isOpen = false
      },
      handleOpenModal() {
        document.body.classList.add('no-scroll')
        this.isOpen = true
      },
      async handleConfirm(deactivate) {
        try {
          if (this.type === 'treatment') {
            await this.cancelTreatments({
              treatmentId: this.treatment.uuid,
              bookingIds: [this.getTreatmentBookingId(this.treatment)],
              guestIndex: this.guestNumber
            })
          } else {
            await this.cancelTreatments({
              cancelAll: true
            })
          }
        } catch (error) {
          console.error(error)
        } finally {
          this.handleCloseModal()
          deactivate()
        }
      },
      getTreatmentBookingId(treatment) {
        const selectedItinerary = this.reservationDetailsData?.guestAvailabilities[this.guestNumber]
        return selectedItinerary?.itinerary?.find((t) => (
          t.treatmentId === treatment.selectedTimeSlot.spaSoftId
        )).bookingId
      }
    }
  }
</script>
